import { useState } from "react";
import { Link } from "react-router-dom";
import { Award, BarChart3, Table2, ArrowRight } from "lucide-react";
import { formatINR, formatINRCompact } from "@/utils/formatINR";

// Categorical slots 1 & 2 — validated pair (CVD ΔE 24.7, normal ΔE 33.6 on a white surface).
const C_NPD = "#2a78d6";
const C_ASM = "#eb6834";
const GRID = "#e2e8f0";
const AXIS_INK = "#64748b";

// Plot geometry — the viewBox height includes the x-axis band so the card never
// grows a nested scrollbar.
const VB_W = 760;
const VB_H = 280;
// left pad holds the widest tick label — these run to "3,56,10,500 Cr" on real data
const PAD = { top: 26, right: 20, bottom: 38, left: 112 };
const PLOT_W = VB_W - PAD.left - PAD.right;
const PLOT_H = VB_H - PAD.top - PAD.bottom;
const BAR_W = 24;      // mark spec: bars never fill the band
const SEG_GAP = 2;     // surface gap between stacked segments
const R = 4;           // rounded data-end
// A non-zero year never renders as literal nothing. One outsized project can put
// Y1 orders of magnitude above the rest; the floor keeps those years visible and
// hoverable, and the axis, tooltip and table view still carry the true values.
const MIN_H = 3;

function niceCeil(v) {
  if (v <= 0) return 1;
  const exp = Math.floor(Math.log10(v));
  const base = Math.pow(10, exp);
  const n = v / base;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return step * base;
}

// Column path: rounded at the data-end, square where it meets the baseline.
function barPath(x, y, w, h, roundTop) {
  if (h <= 0) return "";
  const r = roundTop ? Math.min(R, h, w / 2) : 0;
  return `M ${x} ${y + h} L ${x} ${y + r} Q ${x} ${y} ${x + r} ${y} L ${x + w - r} ${y} Q ${x + w} ${y} ${x + w} ${y + r} L ${x + w} ${y + h} Z`;
}

export default function AwardedOutlook5Y({ outlook = {}, scopeLabel = "All Units", unitFilter = "" }) {
  const [view, setView] = useState("chart");
  const [hover, setHover] = useState(null);

  const years = outlook.by_year || [];
  const capYears = outlook.cap_years || 5;
  const awardedTotal = outlook.awarded_total || 0;
  const topAwarded = (outlook.top_awarded || []).slice(0, 5);
  const hasData = years.some((y) => (y.awarded_total || 0) > 0);

  const maxVal = niceCeil(Math.max(...years.map((y) => y.awarded_total || 0), 0));
  const bandW = PLOT_W / Math.max(years.length, 1);
  const yFor = (v) => PAD.top + PLOT_H - (v / maxVal) * PLOT_H;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * maxVal);
  // The hero scales down for outsized books so it never spills into the plot.
  const heroText = formatINRCompact(awardedTotal, { decimals: Math.abs(awardedTotal) >= 1e11 ? 0 : 2 });
  const heroPx = heroText.length > 15 ? 28 : heroText.length > 12 ? 34 : 46;
  const peakIdx = years.reduce(
    (best, y, i) => ((y.awarded_total || 0) > (years[best]?.awarded_total || 0) ? i : best),
    0
  );

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-4"
      data-testid="awarded-outlook-5y"
    >
      <div className="flex flex-wrap items-start justify-between gap-2 p-3 border-b border-slate-100 bg-gradient-to-r from-amber-50/70 via-orange-50/30 to-transparent">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-sm ring-1 ring-amber-300/50">
            <Award size={20} className="text-white" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Awarded Projects · {capYears}-Year Business Outlook
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Confirmed order book · {scopeLabel} — year-by-year value of awarded NPDs and assemblies,
              each truncated at {capYears} years and priced down by its YoY reduction.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" role="group" aria-label="View mode">
          {[
            { k: "chart", icon: BarChart3, label: "Chart" },
            { k: "table", icon: Table2, label: "Table" },
          ].map(({ k, icon: Icon, label }) => (
            <button
              key={k}
              type="button"
              onClick={() => setView(k)}
              aria-pressed={view === k}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${
                view === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`outlook-view-${k}`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,210px)_minmax(0,1fr)_minmax(0,300px)] gap-4 p-3">
        {/* Hero figure + split */}
        <div className="flex flex-col">
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-amber-50 via-orange-50/50 to-white border border-amber-100 p-3.5 shadow-sm">
            <div className="absolute -right-5 -top-5 w-20 h-20 rounded-full bg-amber-200/30 blur-xl pointer-events-none" aria-hidden="true" />
            <div className="relative flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber-700/80 font-bold">
              <Award size={12} className="text-amber-500" /> Awarded order book · {capYears}y
            </div>
            <div
              className="relative leading-none font-extrabold mt-2 break-words bg-gradient-to-br from-amber-500 to-orange-600 bg-clip-text text-transparent drop-shadow-sm"
              style={{ fontSize: `${heroPx}px` }}
              title={formatINR(awardedTotal)}
              data-testid="awarded-outlook-total"
            >
              {heroText}
            </div>
            <div className="relative text-xs text-slate-500 mt-2">
              Across <span className="font-semibold text-slate-700">{outlook.awarded_count || 0}</span>{" "}
              awarded project{(outlook.awarded_count || 0) === 1 ? "" : "s"}
              {(outlook.awarded_with_value_count || 0) !== (outlook.awarded_count || 0) && (
                <>
                  {" "}· <span className="font-semibold text-slate-700">{outlook.awarded_with_value_count || 0}</span> with
                  volumes captured
                </>
              )}
            </div>
          </div>

          <div className="mt-3 space-y-2.5">
            <SplitRow color={C_NPD} label="NPD costings" value={outlook.awarded_npd_component || 0} total={awardedTotal} />
            <SplitRow color={C_ASM} label="Assemblies" value={outlook.awarded_assembly_component || 0} total={awardedTotal} />
          </div>

          <div className="mt-4 rounded-lg bg-gradient-to-br from-sky-50 to-white border border-sky-100 p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-sky-700/70 font-semibold">
              Still up for grabs ({capYears}y)
            </div>
            <div
              className="text-lg font-bold text-sky-700 mt-0.5"
              title={formatINR(outlook.potential_total || 0)}
            >
              {formatINRCompact(outlook.potential_total || 0)}
            </div>
            <Link
              to={`/npd?awarded=no${unitFilter ? `&unit_id=${unitFilter}` : ""}`}
              className="text-[11px] text-sky-700 hover:text-sky-800 font-medium inline-flex items-center gap-1 mt-1"
            >
              View not-yet-awarded <ArrowRight size={11} />
            </Link>
          </div>
        </div>

        {/* Chart / table */}
        <div className="min-w-0">
          {!hasData ? (
            <div className="h-full min-h-[220px] flex flex-col items-center justify-center text-center border border-dashed border-slate-200 rounded-lg p-6">
              <Award size={22} className="text-slate-300 mb-2" />
              <p className="text-sm font-medium text-slate-700">No awarded value to project yet</p>
              <p className="text-xs text-slate-500 mt-1 max-w-sm">
                A project appears here once it is marked <strong>awarded</strong> and has year-wise volumes
                captured. Awarded records without volumes contribute ₹0.
              </p>
            </div>
          ) : view === "chart" ? (
            <>
              <div className="flex items-center gap-4 mb-1">
                <Legend color={C_NPD} label="NPD costings" />
                <Legend color={C_ASM} label="Assemblies" />
                <span className="text-[10px] text-slate-400 ml-auto">Axis in ₹</span>
              </div>
              {/* The plot scrolls inside its own box on narrow screens rather than
                  shrinking its labels to nothing; the page itself never scrolls sideways. */}
              <div className="overflow-x-auto">
              <div className="relative min-w-[360px]">
                <svg
                  viewBox={`0 0 ${VB_W} ${VB_H}`}
                  className="w-full h-auto"
                  role="img"
                  aria-label={`Awarded business by year for the next ${capYears} years`}
                >
                  <defs>
                    <linearGradient id="npdGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4c97ec" />
                      <stop offset="100%" stopColor="#2a78d6" />
                    </linearGradient>
                    <linearGradient id="asmGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f8915c" />
                      <stop offset="100%" stopColor="#eb6834" />
                    </linearGradient>
                  </defs>
                  {ticks.map((t, i) => (
                    <g key={i}>
                      <line
                        x1={PAD.left}
                        x2={VB_W - PAD.right}
                        y1={yFor(t)}
                        y2={yFor(t)}
                        stroke={i === 0 ? "#cbd5e1" : GRID}
                        strokeWidth="1"
                      />
                      <text
                        x={PAD.left - 10}
                        y={yFor(t) + 4}
                        textAnchor="end"
                        fontSize="11"
                        fill={AXIS_INK}
                        style={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {t === 0 ? "0" : formatINRCompact(t, { withSymbol: false, decimals: 0 })}
                      </text>
                    </g>
                  ))}

                  {years.map((y, i) => {
                    const cx = PAD.left + bandW * i + bandW / 2;
                    const x = cx - BAR_W / 2;
                    const npdV = y.awarded_npd || 0;
                    const asmV = y.awarded_assembly || 0;
                    const base = PAD.top + PLOT_H;
                    const npdH = npdV > 0 ? Math.max((npdV / maxVal) * PLOT_H, MIN_H) : 0;
                    const asmH = asmV > 0 ? Math.max((asmV / maxVal) * PLOT_H, MIN_H) : 0;
                    // 2px surface gap between the two fills, taken off the lower one
                    const gap = npdH > 0 && asmH > 0 ? SEG_GAP : 0;
                    const npdDrawn = Math.max(npdH - gap, 0);
                    const asmY = base - npdH - asmH;
                    const isPeak = i === peakIdx && (y.awarded_total || 0) > 0;
                    return (
                      <g key={y.year_number}>
                        {npdH > 0 && (
                          <path
                            d={barPath(x, base - npdDrawn, BAR_W, npdDrawn, asmH <= 0)}
                            fill="url(#npdGrad)"
                          />
                        )}
                        {asmH > 0 && <path d={barPath(x, asmY, BAR_W, asmH, true)} fill="url(#asmGrad)" />}

                        {isPeak && (() => {
                          const lbl = formatINRCompact(y.awarded_total, { decimals: 1 });
                          const cxp = Math.min(Math.max(cx, PAD.left + 58), VB_W - PAD.right - 58);
                          const w = lbl.length * 6.6 + 16;
                          const yp = base - npdH - asmH - 24;
                          return (
                            <g>
                              <rect x={cxp - w / 2} y={yp} width={w} height={18} rx={9} fill="#fff7ed" stroke="#fdba74" />
                              <text x={cxp} y={yp + 13} textAnchor="middle" fontSize="11" fontWeight="700" fill="#c2410c">
                                {lbl}
                              </text>
                            </g>
                          );
                        })()}

                        <text
                          x={cx}
                          y={base + 18}
                          textAnchor="middle"
                          fontSize="11"
                          fontWeight={hover === i ? "600" : "400"}
                          fill={hover === i ? "#0f172a" : AXIS_INK}
                        >
                          {y.year_label}
                        </text>
                        <text x={cx} y={base + 31} textAnchor="middle" fontSize="9" fill="#94a3b8">
                          {`Y${y.year_number}`}
                        </text>

                        {/* Hit target spans the whole band, not just the 24px mark */}
                        <rect
                          x={PAD.left + bandW * i}
                          y={PAD.top}
                          width={bandW}
                          height={PLOT_H + PAD.bottom - 10}
                          fill="transparent"
                          onMouseEnter={() => setHover(i)}
                          onMouseLeave={() => setHover(null)}
                          onFocus={() => setHover(i)}
                          onBlur={() => setHover(null)}
                          tabIndex={0}
                          style={{ cursor: "pointer", outline: "none" }}
                        >
                          <title>{`${y.year_label}: ${formatINR(y.awarded_total)}`}</title>
                        </rect>
                      </g>
                    );
                  })}
                </svg>

                {hover !== null && years[hover] && (
                  <div
                    className="absolute pointer-events-none bg-slate-900 text-white rounded-lg px-3 py-2 shadow-lg z-10 whitespace-nowrap"
                    style={{
                      left: `${((PAD.left + bandW * hover + bandW / 2) / VB_W) * 100}%`,
                      top: 0,
                      transform: "translate(-50%, -8px)",
                    }}
                  >
                    <div className="text-[11px] font-semibold">{years[hover].year_label}</div>
                    <div className="text-[10px] text-slate-300 mb-1">
                      Year {years[hover].year_number} of {capYears}
                    </div>
                    <TipRow color={C_NPD} label="NPD" value={years[hover].awarded_npd} />
                    <TipRow color={C_ASM} label="Assemblies" value={years[hover].awarded_assembly} />
                    <div className="text-[11px] font-semibold mt-1 pt-1 border-t border-white/20">
                      Total {formatINR(years[hover].awarded_total)}
                    </div>
                  </div>
                )}
              </div>
              </div>
            </>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left p-2.5">Year</th>
                    <th className="text-right p-2.5">NPD</th>
                    <th className="text-right p-2.5">Assemblies</th>
                    <th className="text-right p-2.5">Awarded total</th>
                    <th className="text-right p-2.5">Potential</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {years.map((y) => (
                    <tr key={y.year_number}>
                      <td className="p-2.5">
                        <span className="font-medium text-slate-900">{y.year_label}</span>
                        <span className="text-slate-400 text-xs ml-2">Y{y.year_number}</span>
                      </td>
                      <td className="p-2.5 text-right text-slate-700">{formatINR(y.awarded_npd)}</td>
                      <td className="p-2.5 text-right text-slate-700">{formatINR(y.awarded_assembly)}</td>
                      <td className="p-2.5 text-right font-semibold text-amber-700">{formatINR(y.awarded_total)}</td>
                      <td className="p-2.5 text-right text-sky-700">{formatINR(y.potential_total)}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50 font-semibold">
                    <td className="p-2.5">{capYears}-year total</td>
                    <td className="p-2.5 text-right">{formatINR(outlook.awarded_npd_component)}</td>
                    <td className="p-2.5 text-right">{formatINR(outlook.awarded_assembly_component)}</td>
                    <td className="p-2.5 text-right text-amber-700">{formatINR(awardedTotal)}</td>
                    <td className="p-2.5 text-right text-sky-700">{formatINR(outlook.potential_total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right column — Top awarded list, beside the chart */}
        {topAwarded.length > 0 && (
        <div className="min-w-0 lg:border-l lg:border-slate-100 lg:pl-4" data-testid="awarded-top-list">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Top by {capYears}-yr value
            </h3>
            <Link
              to={`/npd?awarded=yes${unitFilter ? `&unit_id=${unitFilter}` : ""}`}
              className="text-[10px] text-orange-600 hover:text-orange-700 font-medium flex items-center gap-0.5"
            >
              View all <ArrowRight size={11} />
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {topAwarded.map((p) => {
              const pct = awardedTotal > 0 ? (p.five_year_value / awardedTotal) * 100 : 0;
              const color = p.kind === "assembly" ? C_ASM : C_NPD;
              return (
                <Link
                  key={`${p.kind}-${p.id}`}
                  to={p.kind === "assembly" ? `/assemblies/${p.id}` : `/npd/${p.id}`}
                  className="relative flex items-center gap-2.5 pt-1.5 pb-2.5 px-2 -mx-2 rounded-md hover:bg-slate-50 transition"
                  data-testid={`top-awarded-${p.number}`}
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color }}
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-slate-900 truncate">{p.name || p.number}</span>
                      <span className="text-[8px] uppercase tracking-wide bg-slate-100 text-slate-600 px-1 py-0.5 rounded flex-shrink-0">
                        {p.kind === "assembly" ? "ASM" : "NPD"}
                      </span>
                      {p.unit_code && (
                        <span className="text-[9px] font-mono bg-slate-100 text-slate-600 px-1 py-0.5 rounded flex-shrink-0">
                          {p.unit_code}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {p.number} · {p.customer_name} · {p.life_years}y life
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div
                      className="text-[11px] font-semibold text-slate-900 whitespace-nowrap"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                      title={formatINR(p.five_year_value)}
                    >
                      {formatINRCompact(p.five_year_value, {
                        decimals: Math.abs(p.five_year_value) >= 1e11 ? 0 : 2,
                      })}
                    </div>
                    <div className="text-[9px] text-slate-400">{pct.toFixed(1)}%</div>
                  </div>
                  <span className="absolute left-2 right-2 bottom-0.5 h-[3px] rounded-full bg-slate-100 overflow-hidden" aria-hidden="true">
                    <span className="block h-full rounded-full" style={{ width: `${Math.max(2, pct)}%`, backgroundColor: color }} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span className="flex items-center gap-1.5 text-[11px] text-slate-600">
      <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color }} aria-hidden="true" />
      {label}
    </span>
  );
}

function TipRow({ color, label, value }) {
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} aria-hidden="true" />
      <span className="text-slate-300">{label}</span>
      <span className="ml-auto pl-3 font-medium">{formatINR(value)}</span>
    </div>
  );
}

function SplitRow({ color, label, value, total }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center gap-2 text-xs">
        <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} aria-hidden="true" />
        <span className="text-slate-600">{label}</span>
        <span className="ml-auto font-semibold text-slate-900" title={formatINR(value)}>
          {formatINRCompact(value)}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1.5">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}
